
import { defineComponent, onMounted, reactive } from 'vue'

// @components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import NewLimit from '@/common/new-components/NewLimit/index.vue'
import NewPagination from '@/common/new-components/NewPagination/index.vue'
import NewTable from '@/common/new-components/NewTable/index.vue'
import NewButton from '@/ui-kit/NewButton/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'

// @module components
import DirectionsTMSFilters from '@/modules/TMSModule/components/DirectionsTMSFilters/index.vue'
import DirectionsTMSRow from '@/modules/TMSModule/components/DirectionsTMSRow/index.vue'
import DirectionsTMSActions from '@/modules/TMSModule/components/DirectionsTMSActions/index.vue'

// @types
import {
    AddDirectionTMSPayload,
    DirectionTMS,
    GetDirectionsTMSParams,
    UpdateDirectionTMSPayload,
} from '@/new-services/types/tms'

// @new-services
import tmsAPI from '@/new-services/tms.service'
import locationAPI from '@/new-services/location.service'

// @composable
import { useFile } from '@/common/composable/useFile'
import useNotifications from '@/common/composable/useNotifications'
import useAccount from '@/common/composable/useAccount'
import useActions from '@/common/composable/useActions'

const TABLE_COLUMNS = [
    {
        key: 'isParent',
        name: '',
    },
    {
        key: 'direction',
        name: 'Наименование',
    },
    {
        key: 'code',
        name: 'Код 1С',
        isCentered: true,
    },
    {
        key: 'zone',
        name: 'Зона доставки',
        isCentered: true,
    },
    {
        key: 'internalDirectionsCount',
        name: 'Дочерние направления',
        isCentered: true,
    },
    {
        key: 'logistName',
        name: 'Логист',
        isCentered: true,
    },
    {
        key: 'status',
        name: 'Статус',
    },
]

export default defineComponent({
    components: {
        PageTitle,
        DirectionsTMSFilters,
        'directions-tms-row': DirectionsTMSRow,
        NewLimit,
        NewPagination,
        NewTable,
        NewButton,
        Loader,
        'directions-tms-actions': DirectionsTMSActions,
    },
    setup() {
        const { saveFile } = useFile()
        const notifications = useNotifications()
        const { havePermission } = useAccount()
        const { actions_state, startCreate, startUpdate } = useActions<DirectionTMS>()

        const state = reactive({
            list: [] as DirectionTMS[],
            is_excel_loading: false,
            is_loading: false,
            is_failed: false,
            filters: {
                limit: 50,
                current_page: 1,
                total_pages: 1,
                total_items: 1,
                //
                fromCityId: null as number | null,
                whereCityId: null as number | null,
                code: null as string | null,
                zoneId: null as number | null,
                logistId: null as number | null,
                directionTypeId: null as number | null,
                isActive: null as number | null,
            },
        })

        const loadDirections = async () => {
            try {
                state.is_loading = true

                const params: GetDirectionsTMSParams = {
                    page: state.filters.current_page,
                    limit: state.filters.limit,
                    //
                    fromCityId: state.filters.fromCityId,
                    whereCityId: state.filters.whereCityId,
                    code: state.filters.code,
                    zoneId: state.filters.zoneId,
                    logistId: state.filters.logistId,
                    directionTypeId: state.filters.directionTypeId,
                    isActive: state.filters.isActive,
                }

                const response = await tmsAPI.getDirections(params)
                state.list = response.data.data
                state.filters.total_pages = response.data.meta.last_page
                state.filters.total_items = response.data.meta.total

                state.is_failed = false
            } catch (error) {
                state.is_failed = true
                console.error(error)
            } finally {
                state.is_loading = false
            }
        }

        const onChangePage = (new_page: number) => {
            state.filters.current_page = new_page
            loadDirections()
        }

        const onFiltersChanged = (filters: GetDirectionsTMSParams) => {
            state.filters.fromCityId = filters.fromCityId as number | null
            state.filters.whereCityId = filters.whereCityId as number | null
            state.filters.code = filters.code as string | null
            state.filters.zoneId = filters.zoneId as number | null
            state.filters.logistId = filters.logistId as number | null
            state.filters.directionTypeId = filters.directionTypeId as number | null
            state.filters.isActive = filters.isActive as number | null

            onChangePage(1)
        }

        const onFiltersReset = () => {
            state.filters.fromCityId = null
            state.filters.whereCityId = null
            state.filters.code = null
            state.filters.zoneId = null
            state.filters.logistId = null
            state.filters.directionTypeId = null
            state.filters.isActive = null

            onChangePage(1)
        }

        const onLimitChange = (new_limit: number) => {
            state.filters.limit = new_limit
            loadDirections()
        }

        const downloadReport = async () => {
            try {
                state.is_excel_loading = true

                const params = {
                    fromCityId: state.filters.fromCityId,
                    whereCityId: state.filters.whereCityId,
                    code: state.filters.code,
                }

                const response = await tmsAPI.downloadReport(params)

                saveFile(response.data, `Отчет по направлениям`)
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При выгрузке отчета произошла ошибка' })
            } finally {
                state.is_excel_loading = false
            }
        }

        const createDirection = async (payload: AddDirectionTMSPayload) => {
            try {
                actions_state.is_actions_loading = true

                await tmsAPI.addDirectionTMS(payload)
                await loadDirections()

                actions_state.is_actions_opened = false
                notifications.notify({ title: 'Направление успешно создано!', type: 'success' })
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: `Ошибка при создании направления`,
                })
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const updateDirection = async (payload: UpdateDirectionTMSPayload) => {
            if (!actions_state.initial_value) return
            const { id } = actions_state.initial_value

            try {
                actions_state.is_actions_loading = true

                await tmsAPI.updateDirectionTMS(id, payload)

                await loadDirections()

                actions_state.is_actions_opened = false
                notifications.notify({
                    title: 'Направление успешно отредактировано!',
                    type: 'success',
                })
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: `Ошибка при редактировании направления`,
                })
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        //

        onMounted(loadDirections)

        //

        return {
            state,
            actions_state,
            //
            onFiltersChanged,
            onFiltersReset,
            onLimitChange,
            onChangePage,
            loadDirections,
            downloadReport,
            havePermission,
            startCreate,
            startUpdate,
            createDirection,
            updateDirection,
            //
            TABLE_COLUMNS,
        }
    },
})
